/*
 Copyright (c) 2025. BrightNight. All rights reserved.
 Unauthorized copying of this file, via any medium is strictly prohibited.
 Proprietary and confidential.
 */

import CheckIcon from '@mui/icons-material/CheckRounded';
import OfflineBoltIcon from '@mui/icons-material/OfflineBolt';
import ErrorIcon from '@mui/icons-material/PriorityHighRounded';
import {
  Badge,
  type BadgeProps,
  Box,
  CircularProgress,
  ClickAwayListener,
  Fab,
  Paper,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import { green, red } from '@mui/material/colors';
import { alpha, Stack } from '@mui/system';
import { FlowRunState } from '@poweralpha/common';
import { AnimatePresence, motion } from 'framer-motion';
import { sum } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSimulationInfiniteQuery } from './api';
import { PAGE_SIZE, RUN_ENTRY_HEIGHT } from './const';
import SimulationEntry from './SimulationEntry';

const RUN_LIMIT = 100;

const MAX_BOX_HEIGHT = 400;

const SimulationStatusFab = () => {
  const theme = useTheme();
  const hasBeenOpened = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [hasRunSimulations, setHasRunSimulations] = useState(false);
  const [open, setOpen] = useState(false);

  const { data, fetchNextPage, isFetching } = useSimulationInfiniteQuery();

  const transitionDuration = useMemo(
    () => ({
      enter: theme.transitions.duration.enteringScreen,
      exit: theme.transitions.duration.leavingScreen,
    }),
    [theme.transitions.duration],
  );

  const boxHeight = useMemo(
    () =>
      Math.min(
        sum(data?.pages.flatMap((res) => res.rows.length)) * RUN_ENTRY_HEIGHT + RUN_ENTRY_HEIGHT,
        MAX_BOX_HEIGHT,
      ),
    [data?.pages],
  );

  const badgeProps = useMemo((): {
    badgeContent: React.ReactNode;
    color?: BadgeProps['color'];
    badgeSx?: { height: number; width: number; borderRadius: string; bgcolor: string };
  } => {
    if (!hasRunSimulations) {
      return { badgeContent: 0 };
    }

    if (data?.numRunning) {
      return { badgeContent: data.numRunning, color: 'info' };
    }

    const lastRunStatus = data?.pages?.at(-1)?.rows?.at(-1)?.state.type;

    const badgeSx = {
      width: 22,
      height: 22,
      borderRadius: '50%',
    };

    if (lastRunStatus === FlowRunState.CRASHED || lastRunStatus === FlowRunState.FAILED) {
      return {
        badgeContent: <ErrorIcon style={{ fontSize: 20, color: red[800] }} />,
        color: 'error',
        badgeSx: { ...badgeSx, bgcolor: red[200] },
      };
    }

    return {
      badgeContent: <CheckIcon style={{ fontSize: 20, color: green[800] }} />,
      color: 'success',
      badgeSx: { ...badgeSx, bgcolor: green[200] },
    };
  }, [data?.pages, data?.numRunning, hasRunSimulations]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { target } = e as unknown as { target: { scrollTop: number } };
      if (target.scrollTop < 100 && !isFetching) {
        const lastPage = data?.pages.at(-1);
        const currentCount = sum(data?.pages.flatMap((res) => res.rows.length));
        if (currentCount < (lastPage?.count ?? 0) && currentCount < RUN_LIMIT) {
          fetchNextPage().then(() => {
            const el = scrollContainerRef.current;
            if (el) {
              el.scrollTop += RUN_ENTRY_HEIGHT * PAGE_SIZE;
            }
          });
        }
      }
    },
    [data?.pages, fetchNextPage, isFetching],
  );

  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open && scrollContainerRef.current) {
      // scroll to bottom when opened or data updates
      const el = scrollContainerRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, [open]);

  useEffect(() => {
    if (data?.numRunning === undefined || hasBeenOpened.current) return;
    if (data.numRunning > 0) {
      setHasRunSimulations(true);
      hasBeenOpened.current = true;
    }
  }, [data?.numRunning]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'fixed', bottom: 32, right: 46 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }} data-visual-test="transparent">
          <CircularProgress
            variant="indeterminate"
            size={68}
            thickness={4}
            color="secondary"
            data-visual-test="transparent"
            sx={{
              visibility: data?.numRunning && hasRunSimulations ? 'visible' : 'hidden',
              position: 'fixed',
              bottom: 30,
              right: 40.5,
              zIndex: 1301,
            }}
          />
          <Box sx={{ zIndex: 1351 }}>
            <Badge
              badgeContent={badgeProps.badgeContent}
              color={badgeProps?.color}
              overlap="circular"
              sx={{
                cursor: 'pointer',
                '& .MuiBadge-badge': { ...badgeProps?.badgeSx, zIndex: 1350 },
              }}
            >
              <Zoom in={hasRunSimulations} timeout={transitionDuration} unmountOnExit>
                <Fab
                  color={data?.numRunning ? 'info' : 'success'}
                  onClick={() => setOpen((o) => !o)}
                  data-testid="simulation-status-fab"
                  sx={{
                    zIndex: 1300,
                    mb: '4px',
                    bgcolor: alpha(theme.palette.secondary.main, 0.2),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.secondary.main, 0.3),
                    },
                  }}
                >
                  <OfflineBoltIcon color="secondary" fontSize="large" />
                </Fab>
              </Zoom>
            </Badge>
          </Box>
        </Box>
        <Box sx={{ zIndex: 1300 }}>
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ width: 0, height: 0, opacity: 0 }}
                animate={{ width: 300, height: boxHeight, opacity: 1 }}
                exit={{ width: 0, height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  transformOrigin: 'bottom right',
                  overflow: 'hidden',
                  zIndex: 1200,
                }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    width: '100%',
                    height: 'calc(100% - 4px)',
                    boxSizing: 'border-box',
                    borderRadius: 8,
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <Stack>
                    <Box
                      ref={scrollContainerRef}
                      onScroll={onScroll}
                      sx={{
                        height: boxHeight - RUN_ENTRY_HEIGHT - 4,
                        overflowY: boxHeight === MAX_BOX_HEIGHT ? 'auto' : 'hidden',
                        overflowX: 'hidden',
                      }}
                    >
                      <Paper>
                        {data?.pages.map((page) => (
                          <Stack key={`page-${page.rows?.[0].id}`}>
                            {page.rows.map((run) => (
                              <SimulationEntry key={run.id} run={run} onClick={handleClose} />
                            ))}
                          </Stack>
                        ))}
                      </Paper>
                    </Box>
                    <Box
                      sx={{
                        mt: 1,
                        ml: 2,
                      }}
                    >
                      <Typography variant="h6">Your simulations</Typography>
                    </Box>
                  </Stack>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default SimulationStatusFab;
